import React, { useMemo, useState } from "react";
import { Check, Folder, Loader2, Search, X } from "lucide-react";

import ModalDialog from "../ModalDialog";
import StatusPill from "../StatusPill";
import type { FolderTreeNode } from "../../services/fileApi";
import { countDealFiles, suggestDeal } from "../../utils/addFilesReview";

interface DealDestinationDialogProps {
	files: File[];
	/** Deals the files could go into — root folders that belong to a project. */
	deals: FolderTreeNode[];
	loading: boolean;
	onClose: () => void;
	onChooseDeal: (deal: FolderTreeNode) => void;
	onStartNew: () => void;
}

type Destination = "existing" | "new";

const TITLE_ID = "deal-destination-title";

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * The first question after picking files on Data Sources (DOP-608): add them
 * to a deal that already exists, or start a new one. When the files share
 * names with a deal's files, that deal is suggested and preselected.
 */
const DealDestinationDialog: React.FC<DealDestinationDialogProps> = ({
	files,
	deals,
	loading,
	onClose,
	onChooseDeal,
	onStartNew,
}) => {
	const suggestion = useMemo(() => suggestDeal(files, deals), [files, deals]);
	const [destination, setDestination] = useState<Destination | null>(null);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [query, setQuery] = useState("");

	// Until the analyst chooses, the suggestion decides: its deal if there is
	// one, otherwise a new deal (what this page has always done).
	const effectiveDestination: Destination =
		destination ?? (suggestion ? "existing" : "new");
	const effectiveSelectedId = selectedId ?? suggestion?.deal.folder.id ?? null;

	const visibleDeals = useMemo(() => {
		const needle = query.trim().toLowerCase();
		const ordered = [...deals].sort((a, b) => {
			if (a === suggestion?.deal) return -1;
			if (b === suggestion?.deal) return 1;
			return b.folder.updated_at.localeCompare(a.folder.updated_at);
		});
		return needle
			? ordered.filter((deal) => deal.folder.name.toLowerCase().includes(needle))
			: ordered;
	}, [deals, query, suggestion]);

	const selectedDeal =
		deals.find((deal) => deal.folder.id === effectiveSelectedId) ?? null;
	const canContinue =
		effectiveDestination === "new" || (!loading && selectedDeal !== null);

	const handleContinue = () => {
		if (effectiveDestination === "new") onStartNew();
		else if (selectedDeal) onChooseDeal(selectedDeal);
	};

	const radioCard = (active: boolean) =>
		`rounded-xl border p-4 transition-colors ${
			active ? "border-2 border-primary-500 bg-primary-50/30" : "border-gray-200"
		}`;

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onClose} size="lg">
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-xl font-semibold text-text-base">
						Where should {files.length === 1 ? "this file" : `these ${files.length} files`} go?
					</h2>
					<p className="mt-1 text-sm text-text-muted">
						Add {files.length === 1 ? "it" : "them"} to a deal you already have, or
						start a new one.
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="rounded-md p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					<X className="h-[18px] w-[18px]" aria-hidden="true" />
				</button>
			</div>

			<div className="mt-5 flex flex-col gap-3">
				<div className={radioCard(effectiveDestination === "existing")}>
					<label className="flex cursor-pointer items-center gap-2.5 text-base font-semibold text-text-base">
						<input
							type="radio"
							name="deal-destination"
							checked={effectiveDestination === "existing"}
							onChange={() => setDestination("existing")}
							className="h-4 w-4 border-gray-300 text-primary focus:ring-primary-500"
						/>
						Add to a deal I already have
					</label>

					{effectiveDestination === "existing" && (
						<div className="mt-3 flex flex-col gap-2">
							<label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-text-muted focus-within:border-primary-500">
								<Search className="h-4 w-4" aria-hidden="true" />
								<input
									type="text"
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									placeholder="Search your deals"
									aria-label="Search your deals"
									className="flex-1 border-0 bg-transparent p-0 text-base text-text-base placeholder:text-text-muted focus:outline-none focus:ring-0"
								/>
							</label>
							{loading ? (
								<div className="flex items-center justify-center py-6">
									<Loader2 className="h-4 w-4 animate-spin text-text-muted" aria-label="Loading your deals" />
								</div>
							) : visibleDeals.length === 0 ? (
								<p className="py-4 text-center text-sm text-text-muted">
									No deals match “{query}”.
								</p>
							) : (
								<ul className="flex max-h-60 flex-col gap-2 overflow-y-auto">
									{visibleDeals.map((deal) => {
										const selected = deal.folder.id === effectiveSelectedId;
										const isSuggested = deal === suggestion?.deal;
										const count = countDealFiles(deal);
										return (
											<li key={deal.folder.id}>
												<button
													type="button"
													aria-pressed={selected}
													onClick={() => setSelectedId(deal.folder.id)}
													className={`flex w-full items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
														selected
															? "border-primary-500 bg-primary-50"
															: "border-gray-200 bg-white hover:bg-gray-50"
													}`}
												>
													<Folder
														className={`h-[18px] w-[18px] flex-shrink-0 ${selected ? "text-primary" : "text-text-muted"}`}
														aria-hidden="true"
													/>
													<span className="flex min-w-0 flex-1 flex-col gap-0.5">
														<span className="flex items-center gap-2">
															<span
																className={`truncate text-base ${selected ? "font-semibold text-primary" : "font-medium text-text-strong"}`}
															>
																{deal.folder.name}
															</span>
															{isSuggested && <StatusPill tone="green" label="Suggested" />}
														</span>
														<span className="text-xs text-text-muted">
															{plural(count, "file")}
															{isSuggested && suggestion
																? ` · ${suggestion.matches} of your files ${suggestion.matches === 1 ? "has a name that matches a file" : "have names that match files"} already here`
																: ""}
														</span>
													</span>
													{selected && (
														<Check className="h-[18px] w-[18px] flex-shrink-0 text-primary" aria-hidden="true" />
													)}
												</button>
											</li>
										);
									})}
								</ul>
							)}
						</div>
					)}
				</div>

				<label className={`${radioCard(effectiveDestination === "new")} flex cursor-pointer items-center gap-2.5`}>
					<input
						type="radio"
						name="deal-destination"
						checked={effectiveDestination === "new"}
						onChange={() => setDestination("new")}
						className="h-4 w-4 border-gray-300 text-primary focus:ring-primary-500"
					/>
					<span className="flex flex-col gap-0.5">
						<span className="text-base font-semibold text-text-base">Start a new deal</span>
						<span className="text-xs text-text-muted">
							Use this when the files are for a different company or transaction.
						</span>
					</span>
				</label>
			</div>

			<div className="mt-5 flex justify-end gap-2.5 border-t border-gray-100 pt-4">
				<button
					type="button"
					onClick={onClose}
					className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					Cancel
				</button>
				<button
					type="button"
					disabled={!canContinue}
					onClick={handleContinue}
					className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-[#003a3a] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{effectiveDestination === "existing" && selectedDeal
						? `Continue to ${selectedDeal.folder.name}`
						: "Continue"}
				</button>
			</div>
		</ModalDialog>
	);
};

export default DealDestinationDialog;
